import React, { createContext, useContext, useMemo, useEffect } from 'react';
import translations from './translations';
import detectLanguageFromTimezone from './detectLanguage';

const LanguageContext = createContext();

const FR_DOMAIN = 'fr.bibliona.com';
const EN_DOMAIN = 'en.bibliona.com';

function getLanguageFromHostname() {
  const hostname = window.location.hostname;

  if (hostname === FR_DOMAIN || hostname === `www.${FR_DOMAIN}`) {
    return 'fr';
  }
  if (hostname === EN_DOMAIN || hostname === `www.${EN_DOMAIN}`) {
    return 'en';
  }

  return null;
}

function resolveLanguage() {
  const buildLang = process.env.REACT_APP_LANG;
  if (buildLang === 'fr' || buildLang === 'en') {
    return buildLang;
  }

  const domainLang = getLanguageFromHostname();
  if (domainLang) return domainLang;

  return detectLanguageFromTimezone();
}

export function LanguageProvider({ children }) {
  const lang = useMemo(() => resolveLanguage(), []);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isProduction = hostname.endsWith('bibliona.com');
    const domainLang = getLanguageFromHostname();

    if (isProduction && !domainLang) {
      const detected = detectLanguageFromTimezone();
      const targetDomain = detected === 'fr' ? FR_DOMAIN : EN_DOMAIN;
      const { protocol, pathname, search, hash } = window.location;
      window.location.replace(`${protocol}//${targetDomain}${pathname}${search}${hash}`);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useMemo(() => {
    return (key) => {
      const entry = translations[key];
      if (!entry) {
        console.warn(`Missing translation key: "${key}"`);
        return key;
      }
      return entry[lang] || entry['en'] || key;
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, t }), [lang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}
